import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatCurrency, formatDate, formatVal } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { Badge } from "../components/ui/badge";
import { BillEditDialog } from "../components/BillEditDialog";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  AlertTriangle, CheckCircle2, ChevronLeft, ChevronRight, Pencil,
  ArrowLeft, Loader2, FileText, ClipboardCheck, ExternalLink, RotateCcw,
} from "lucide-react";

const SEVERITY_STYLES = {
  warning: "text-amber-700 bg-amber-50 border-amber-200",
  info: "text-sky-700 bg-sky-50 border-sky-200",
};

export default function ReviewQueue() {
  const navigate = useNavigate();
  const { isWriter } = useAuth();
  const [bills, setBills] = useState(null);
  const [reviewedBills, setReviewedBills] = useState([]);
  const [idx, setIdx] = useState(0);
  const [total, setTotal] = useState(0);
  const [editOpen, setEditOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [res, reviewedRes] = await Promise.all([
        api.listBills({ needs_review: true }),
        api.listBills({ reviewed: true }),
      ]);
      const rows = (res.data || []).slice().sort(
        (a, b) => (b.review?.issue_count || 0) - (a.review?.issue_count || 0)
      );
      setBills(rows);
      setTotal(rows.length);
      setIdx(0);
      const rev = (reviewedRes.data || []).slice().sort(
        (a, b) => (b.review?.reviewed_at || "").localeCompare(a.review?.reviewed_at || "")
      );
      setReviewedBills(rev);
    } catch (e) {
      toast.error("Could not load the review queue");
      setBills([]);
      setReviewedBills([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const current = bills && bills[idx];
  const remaining = bills ? bills.length : 0;
  const done = Math.max(0, total - remaining);
  const progress = total > 0 ? Math.round((done / total) * 100) : 0;

  const removeCurrent = (id) => {
    setBills((prev) => {
      const next = prev.filter((b) => b.id !== id);
      setIdx((i) => Math.min(i, Math.max(0, next.length - 1)));
      return next;
    });
  };

  const goPrev = () => setIdx((i) => Math.max(0, i - 1));
  const goNext = () => setIdx((i) => Math.min((bills?.length || 1) - 1, i + 1));

  const markReviewed = async () => {
    if (!current) return;
    setBusy(true);
    try {
      await api.markBillReviewed(current.id, true);
      toast.success("Marked as reviewed");
      removeCurrent(current.id);
      await load();
    } catch (e) {
      toast.error("Could not mark as reviewed");
    } finally {
      setBusy(false);
    }
  };

  const undoReviewed = async (bill) => {
    try {
      await api.markBillReviewed(bill.id, false);
      toast.success("Moved back to review queue");
      await load();
    } catch (e) {
      toast.error("Could not update the bill");
    }
  };

  const onSaved = (updated) => {
    if (updated && updated.review && !updated.review.needs_review) {
      removeCurrent(updated.id);
      toast.success("Issues resolved — removed from queue");
    } else if (updated) {
      setBills((prev) => prev.map((b) => (b.id === updated.id ? updated : b)));
    }
  };

  const issues = current?.review?.issues || [];

  const pdfUrl = useMemo(
    () => (current ? api.documentFileUrl(current.document_id) : null),
    [current]
  );

  if (bills === null) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground" data-testid="review-loading">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading review queue…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="review-queue-page">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => navigate("/")} data-testid="review-back-btn">
          <ArrowLeft className="h-4 w-4" /> Dashboard
        </Button>
        <div className="flex items-center gap-2">
          <ClipboardCheck className="h-5 w-5 text-primary" />
          <h1 className="text-xl font-heading font-bold tracking-tight">Review queue</h1>
        </div>
        {total > 0 && (
          <Badge variant="secondary" className="ml-1" data-testid="review-remaining-badge">
            {remaining} remaining
          </Badge>
        )}
      </div>

      {total > 0 && (
        <div className="space-y-1.5">
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>{done} of {total} cleared</span>
            <span>{progress}%</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>
      )}

      {remaining === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center gap-3 py-16 text-center" data-testid="review-empty">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
              <CheckCircle2 className="h-7 w-7" />
            </div>
            <div className="text-lg font-heading font-semibold">All caught up!</div>
            <p className="text-sm text-muted-foreground max-w-sm">
              No bills currently need review. Newly uploaded bills with missing fields, inconsistent totals, or duplicate transactions will show up here.
            </p>
            <Button onClick={() => navigate("/")} className="mt-2" data-testid="review-goto-dashboard">Back to dashboard</Button>
          </CardContent>
        </Card>
      ) : current ? (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* PDF preview */}
            <Card className="overflow-hidden">
              <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-secondary/40">
                <span className="text-xs font-medium text-muted-foreground truncate flex items-center gap-1.5">
                  <FileText className="h-3.5 w-3.5" /> {formatVal(current.source_filename || current.filename)}
                </span>
                <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-primary inline-flex items-center gap-1 hover:underline" data-testid="review-open-pdf">
                  Open <ExternalLink className="h-3 w-3" />
                </a>
              </div>
              <iframe
                key={current.id}
                title="Bill PDF"
                src={pdfUrl}
                className="w-full bg-muted"
                style={{ height: "62vh", border: "none" }}
                data-testid="review-pdf-frame"
              />
            </Card>

            {/* Details + issues */}
            <Card>
              <CardContent className="space-y-4 py-5" data-testid="review-detail-panel">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-lg font-heading font-semibold truncate">{formatVal(current.issuing_authority)}</div>
                    <div className="text-sm text-muted-foreground">
                      {formatVal(current.license_plate)}{current.plate_state ? ` · ${current.plate_state}` : ""}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-xl font-semibold tabular-nums">{formatCurrency(current.total_amount_due, current.currency)}</div>
                    <div className="text-xs text-muted-foreground">Due {formatDate(current.due_date)}</div>
                  </div>
                </div>

                {/* Issues */}
                <div className="space-y-2">
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">Why this needs review</div>
                  {issues.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No specific issues listed.</p>
                  ) : (
                    issues.map((iss, i) => (
                      <div key={`${iss.field || "issue"}-${iss.message}-${i}`} className={`flex items-start gap-2 rounded-md border px-3 py-2 text-sm ${SEVERITY_STYLES[iss.severity] || SEVERITY_STYLES.info}`} data-testid="review-issue">
                        <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                        <span>{iss.message}</span>
                      </div>
                    ))
                  )}
                </div>

                {/* Quick fields */}
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm pt-1">
                  <Field label="Invoice #" value={current.invoice_number} />
                  <Field label="Account ID" value={current.account_id} />
                  <Field label="Bill date" value={formatDate(current.bill_date)} />
                  <Field label="Toll amount" value={formatCurrency(current.toll_amount, current.currency)} />
                  <Field label="Fees" value={formatCurrency((current.processing_fee || 0) + (current.late_fees || 0), current.currency)} />
                  <Field label="Transactions" value={(current.transactions || []).length} />
                </div>

                {isWriter && (
                  <div className="flex flex-wrap gap-2 pt-2 border-t border-border">
                    <Button variant="outline" className="gap-1.5" onClick={() => setEditOpen(true)} data-testid="review-edit-btn">
                      <Pencil className="h-4 w-4" /> Edit fields
                    </Button>
                    <Button className="gap-1.5" onClick={markReviewed} disabled={busy} data-testid="review-mark-btn">
                      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                      Mark reviewed
                    </Button>
                  </div>
                )}
                {!isWriter && (
                  <p className="text-xs text-muted-foreground pt-2 border-t border-border">You have read-only access. Ask an Admin or Member to resolve or mark these bills.</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Navigation footer */}
          <div className="flex items-center justify-between gap-3">
            <Button variant="outline" size="sm" className="gap-1.5" onClick={goPrev} disabled={idx === 0} data-testid="review-prev-btn">
              <ChevronLeft className="h-4 w-4" /> Previous
            </Button>
            <span className="text-sm text-muted-foreground tabular-nums" data-testid="review-position">
              {idx + 1} of {bills.length}
            </span>
            <Button variant="outline" size="sm" className="gap-1.5" onClick={goNext} disabled={idx >= bills.length - 1} data-testid="review-next-btn">
              Skip / Next <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          <BillEditDialog
            bill={current}
            open={editOpen}
            onOpenChange={setEditOpen}
            onSaved={onSaved}
          />
        </>
      ) : null}

      {/* Reviewed docs */}
      {reviewedBills.length > 0 && (
        <Card data-testid="reviewed-docs-section">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
            <h2 className="text-sm font-heading font-semibold">Reviewed docs</h2>
            <Badge variant="secondary" data-testid="reviewed-count-badge">{reviewedBills.length}</Badge>
          </div>
          <CardContent className="p-0 divide-y divide-border">
            {reviewedBills.map((b) => (
              <div key={b.id} className="flex items-center gap-3 px-4 py-3" data-testid="reviewed-doc-row">
                <button
                  type="button"
                  onClick={() => navigate(`/documents/${b.document_id}`)}
                  className="flex min-w-0 flex-1 items-center gap-3 text-left rounded-md -mx-1 px-1 py-0.5 transition-colors hover:bg-[hsl(210_25%_96%)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  data-testid="reviewed-doc-open"
                >
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
                    <CheckCircle2 className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{formatVal(b.issuing_authority)}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {formatVal(b.license_plate)}
                      {b.due_date ? ` · due ${formatDate(b.due_date)}` : ""}
                    </div>
                  </div>
                  <div className="hidden sm:block w-44 shrink-0 px-2" data-testid="reviewed-doc-reviewer">
                    <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Reviewed by</div>
                    <div className="text-sm font-medium truncate">{formatVal(b.review?.reviewed_by)}</div>
                    {b.review?.reviewed_at && (
                      <div className="text-xs text-muted-foreground">{formatDate(b.review.reviewed_at)}</div>
                    )}
                  </div>
                  <div className="text-sm font-semibold tabular-nums shrink-0 w-24 text-right">{formatCurrency(b.total_amount_due, b.currency)}</div>
                </button>
                {isWriter && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="gap-1.5 shrink-0 text-muted-foreground"
                    onClick={() => undoReviewed(b)}
                    data-testid="reviewed-doc-undo"
                  >
                    <RotateCcw className="h-3.5 w-3.5" /> Undo
                  </Button>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

const Field = ({ label, value }) => (
  <div>
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className="tabular-nums">{value === null || value === undefined || value === "" ? "\u2014" : value}</div>
  </div>
);
